/**
 * The mapping layer's data contracts.
 *
 * PURE by construction: nothing in src/mapping imports from src/audio or from
 * any vision code, and nothing here touches the DOM. That is what makes the
 * instrument testable against recorded fixtures instead of against a camera and
 * your own hands (architecture.md §2, invariant 2).
 */

import type { StrokeId } from '../music/talas';

export type Vec3 = readonly [number, number, number];

export interface Hand {
  points: Vec3[]; // 21 MediaPipe landmarks
  handedness: 'left' | 'right'; // already corrected for mirroring
  score: number;
}

/** All normalized 0..1 post-calibration, except `angle` which is radians. */
export interface Features {
  aperture: number;
  height: number;
  roll: number;
  spread: number;
  /** Wrist angle about the frame centre — the platter axis for the scratch scene. */
  angle: number;
  /**
   * The doc's fist test: all fingertips within 0.6 of the palm centroid.
   * A POSE, not a scalar — it debounces by dwell, never by threshold gap.
   * The pad has no fingers and always reports false.
   */
  fistPose: boolean;
  present: boolean;
  confident: boolean;
}

export const NEUTRAL_FEATURES: Features = {
  aperture: 1, height: 0, roll: 0.5, spread: 0.5, angle: 0, fistPose: false,
  present: false, confident: false,
};

/** One frame's worth of input: an expression hand and an optional pitch hand. */
export interface FeatureFrame {
  expr: Features;
  pitch?: Features;
  /** Seconds since the previous frame. */
  dt: number;
  /**
   * Whether this SOURCE can ever supply a pitch hand. The pad cannot; a
   * two-hand camera can. Distinct from `pitch` being absent on one frame:
   * absence on a pitch-capable source is a dropout and latches the degree,
   * while a source with no pitch channel must never latch — treating the
   * pad's every frame as a dropout was how `latched` became permanently
   * true and the degree froze at 0 for every pad user.
   */
  sourceHasPitch: boolean;
}

export type SceneId = 'bass' | 'arp' | 'breaks' | 'scratch' | 'tala' | 'warble';

export interface TriggerEvent {
  kind: 'note' | 'stroke' | 'slice' | 'stab';
  /** Scene-specific payload index: degree, slice number, stroke slot. */
  index: number;
  velocity: number;
}

/**
 * The scene-independent control surface — every DISCRETE decision the
 * vocabulary owns: which degree, which octave, what is held, what fired.
 * Continuous shaping (cutoff, level, resonance…) deliberately does not live
 * here: it flows Features → mapper → intent, per scene. The previous shape
 * carried cutoff/resonance/level/blend too, and every one of those fields was
 * write-only — computed each frame, consumed by nothing.
 */
export interface ControlState {
  degree: number;
  octave: number;
  /** Pitch hand dropped; degree frozen at its last confident value. */
  latched: boolean;
  /** Fist damp — held, suppresses pinch on the same hand. */
  fist: boolean;
  /** Held state of the expression pinch — the grab, the platter hand. */
  pinch: boolean;
  /** One frame, kill everything. Produced by the vocabulary in Phase D. */
  panic: boolean;
  triggers: TriggerEvent[];
}

export const INITIAL_CONTROL: ControlState = {
  degree: 0, octave: 0, latched: false, fist: false, pinch: false, panic: false, triggers: [],
};

/* ------------------------------------------------------------------ */
/* Typed scene intents — what a mapper produces, and all an adapter is  */
/* allowed to act on. Every field is already in engine units.           */
/* ------------------------------------------------------------------ */

export interface BassIntent {
  kind: 'bass';
  cutoffHz: number;
  resonance: number;
  drive: number;
  level: number;
  degree: number;
  octave: number;
  /** True on the frame a note should be armed. */
  trigger: boolean;
  accent: boolean;
}

export interface ArpIntent {
  kind: 'arp';
  cutoffHz: number;
  resonance: number;
  level: number;
  /** Chord degree the sequence is built on. */
  degree: number;
  octaves: number;
  /** Gate length as a fraction of a step. */
  gate: number;
  running: boolean;
}

export interface BreaksIntent {
  kind: 'breaks';
  level: number;
  /** Playback rate multiplier for the slice player, ~0.71..1.41 (half an octave either side). */
  rate: number;
  /**
   * The absolute slice the height axis is pointing at (bar * slicesPerBar +
   * degree) — what a pinch WOULD fire. Always valid, drives the preview.
   */
  selectedSlice: number;
  /** The slice to fire THIS frame (== selectedSlice on a trigger), or -1. */
  triggerSlice: number;
  stutter: boolean;
}

export interface ScratchIntent {
  kind: 'scratch';
  /** Signed playback rate, -8..8. */
  rate: number;
  /** True while the cut gesture is held — a rapid mute, not a fade. */
  cut: boolean;
  level: number;
}

export interface TalaIntent {
  kind: 'tala';
  level: number;
  /** Sruti trim in cents. */
  cents: number;
  /** Velocity scaling for the whole avartanam. */
  velocity: number;
  running: boolean;
  /** True on the frame the free-strike gesture fired. */
  strike: boolean;
  /**
   * Which stroke a strike plays — selected by pitch-hand height across all
   * eight of STROKE_ORDER. The name (not an index) so the scene cannot
   * disagree with the mapper about ordering.
   */
  stroke: StrokeId;
}

export interface WarbleIntent {
  kind: 'warble';
  /** Air-sac pressure. AMIN at rest — below every phonation threshold, so the
   *  rest state is the model's own silence, not a gain. */
  alpha: number;
  /** Labial tension — the pitch axis. Rung-quantized in PERCH, log-continuous
   *  in PLANE. */
  beta: number;
  /** The overblow rim: fist drives the source↔tract fold. */
  fold: number;
  /** Roll → added jitter on the tension. Honest noise. */
  grit: number;
  /** Expression hand present — gates env. */
  sounding: boolean;
  /** True on the frame a pinch should re-attack the note. */
  rearticulate: boolean;
}

export type SceneIntent = BassIntent | ArpIntent | BreaksIntent | ScratchIntent | TalaIntent | WarbleIntent;
